/**
 * Texas State-Specific Diagnostic Questions
 * CORRECTED VERSION - Verified against actual TX regulations
 * 
 * CORRECTIONS MADE:
 * 1. TX-A5: Changed 26 TAC 558.261 → 26 TAC 558.287 (QAPI)
 *    - Section 558.261 does not exist in Chapter 558
 *    - Correct citation: 26 TAC 558.287 "Quality Assessment and Performance Improvement"
 * 
 * 2. TX-A6: Changed 26 TAC 558.262 → 26 TAC 558.256 (Emergency Preparedness)
 *    - Section 558.262 does not exist in Chapter 558
 *    - Correct citation: 26 TAC 558.256 "Emergency Preparedness Planning and Implementation"
 * 
 * VERIFIED REGULATORY FRAMEWORK:
 * - Statutory Authority: Texas Health & Safety Code Chapter 142
 * - Administrative Rules: 26 TAC Chapter 558 (transferred from 40 TAC Chapter 97 in May 2019)
 * - Regulatory Body: Texas Health and Human Services Commission (HHSC)
 * - License Type: Home and Community Support Services Agency (HCSSA)
 * 
 * KEY VERIFIED CITATIONS:
 * - 26 TAC 558.244: Administrator Qualifications and Conditions
 * - 26 TAC 558.245: Staffing Policies
 * - 26 TAC 558.246: Personnel Records
 * - 26 TAC 558.247: Criminal History Checks
 * - 26 TAC 558.256: Emergency Preparedness Planning and Implementation
 * - 26 TAC 558.259: Initial Educational Training in Administration
 * - 26 TAC 558.260: Continuing Education in Administration
 * - 26 TAC 558.287: Quality Assessment and Performance Improvement (QAPI)
 * - 26 TAC 558.301: Client Records
 * - 26 TAC 558.404: Personal Assistance Services Standards
 * - 26 TAC Chapter 561: Employee Misconduct Registry (EMR)
 * - HSC Chapter 250: Nurse Aide Registry and Criminal History Checks
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const TEXAS_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'TX-A1',
    itemId: 'A1',
    stateCode: 'TX',
    title: 'HHSC Home and Community Support Services Agency (HCSSA) License Status',
    description: 'Texas requires all home care organizations to be licensed by HHSC under Health & Safety Code Chapter 142',
    severity: 'high',
    regulationCode: 'HSC Chapter 142',
    regulationName: 'Home and Community Support Services',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov/providers/long-term-care-providers/home-community-support-services-agencies-hcssa',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active HCSSA license from HHSC with Personal Assistance Services (PAS) category?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'HCSSA licenses must be renewed every 2 years. Check your license status in TULIP.',
      },
      {
        id: 'q2',
        question: 'What is your HCSSA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your HHSC license certificate or in TULIP',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your HHSC HCSSA license is current and active per Health & Safety Code Chapter 142.',
      atRisk: 'Your HCSSA license expires within 120 days. Texas requires renewal applications through TULIP before expiration. Submit your renewal immediately.',
      nonCompliant: 'Operating a home care organization in Texas without a valid HCSSA license violates Health & Safety Code Chapter 142 and can result in civil penalties. Contact HHSC at (512) 438-2630.',
      unknown: 'Verify your HCSSA license status in TULIP or call HHSC at (512) 438-2630.',
    },
    stateNotes: [
      'HCSSA licenses are renewed every 2 years',
      'Applications submitted through TULIP (Texas Unified Licensure Information Portal)',
      'Initial survey required within 6 months of license issuance',
      'PAS category is for non-medical personal assistance services',
    ],
  },

  {
    id: 'TX-A2',
    itemId: 'A2',
    stateCode: 'TX',
    title: 'Administrator Training Requirements (26 TAC 558.259-260)',
    description: 'Texas requires HCSSA administrators to complete specific training courses',
    severity: 'high',
    regulationCode: '26 TAC 558.259-260',
    regulationName: 'Administrator Training Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov/providers/long-term-care-providers/home-community-support-services-agencies-hcssa',
    questions: [
      {
        id: 'q1',
        question: 'Has your administrator completed the required 8-hour HHSC-approved initial training course?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required within 12 months prior to appointment per 26 TAC 558.259(c)',
      },
      {
        id: 'q2',
        question: 'Has your administrator completed the additional 16-hour first-year training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Not yet (within first 12 months)' },
        ],
        required: true,
        helpText: 'Required prior to appointment or within 12 months after per 26 TAC 558.259(d)',
      },
      {
        id: 'q3',
        question: 'Does your administrator complete 12 hours of continuing education annually?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required every 12 months after appointment per 26 TAC 558.260(a)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator training meets Texas 26 TAC 558.259-260 requirements (8-hour initial + 16-hour first year + 12-hour annual CE).',
      atRisk: 'Administrator training may be incomplete. Ensure all required training is documented per 26 TAC 558.259-260.',
      nonCompliant: 'Administrator has not completed required 8-hour initial training course. This violates 26 TAC 558.259(c). Complete training immediately.',
      unknown: 'Review administrator training records against 26 TAC 558.259-260 requirements.',
    },
    stateNotes: [
      '8-hour initial course required within 12 months PRIOR to appointment',
      'Additional 16 hours required prior to or within 12 months AFTER appointment (total 24 hours first year)',
      '12 hours continuing education required annually after first year',
      'Alternate administrator has same training requirements',
    ],
  },

  {
    id: 'TX-A3',
    itemId: 'A3',
    stateCode: 'TX',
    title: 'Alternate Administrator Designation',
    description: 'Texas requires an alternate administrator to be designated',
    severity: 'high',
    regulationCode: '26 TAC 558.244',
    regulationName: 'Administrator',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a designated alternate administrator who can act when the primary administrator is unavailable?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Required per 26 TAC 558.244 for client care and state survey attendance',
      },
      {
        id: 'q2',
        question: 'Has your alternate administrator completed the required training?',
        inputType: 'select',
        options: [
          { value: 'complete', label: 'Yes, all required training completed' },
          { value: 'partial', label: 'Some training completed' },
          { value: 'none', label: 'No training completed' },
        ],
        required: true,
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'complete' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'partial' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Alternate administrator is designated and properly trained per 26 TAC 558.244.',
      atRisk: 'Alternate administrator needs to complete required training. Ensure compliance with 26 TAC 558.259-260.',
      nonCompliant: 'No alternate administrator designated. This violates 26 TAC 558.244. Designate and train an alternate immediately.',
      unknown: 'Review alternate administrator designation and training records.',
    },
    stateNotes: [
      'Alternate administrator must be designated per 26 TAC 558.244(b) to act when primary is unavailable',
      'Alternate must complete same training as primary administrator (26 TAC 558.259-260)',
      'Alternate must be available to participate in HHSC surveys when primary administrator is absent',
      'Document alternate designation in writing and maintain in agency records',
    ],
  },

  {
    id: 'TX-A4',
    itemId: 'A4',
    stateCode: 'TX',
    title: 'Written Policies and Procedures (26 TAC 558.245)',
    description: 'Texas requires specific written policies per 26 TAC 558.245',
    severity: 'medium',
    regulationCode: '26 TAC 558.245',
    regulationName: 'Staffing Policies',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Texas-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'client_rights', label: 'Client rights' },
          { value: 'service_agreement', label: 'Service agreement/individual service plan requirements' },
          { value: 'attendant_duties', label: 'Attendant duties and limitations' },
          { value: 'supervision', label: 'Supervision of personnel (26 TAC 558.404(g))' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Criminal history check procedures' },
          { value: 'training', label: 'Orientation and training requirements' },
          { value: 'abuse_reporting', label: 'Abuse/neglect/exploitation reporting' },
          { value: 'orderly_transfer', label: 'Orderly transfer of care plan' },
          { value: 'emergency', label: 'Emergency preparedness plan' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Texas 26 TAC 558.245 requirements.',
      atRisk: 'Your policy manual is missing required sections per 26 TAC 558.245. HHSC surveyors will review your policies for completeness. Missing policies: {missing}.',
      nonCompliant: 'Multiple required policies are missing per 26 TAC 558.245. This is a common HHSC survey deficiency. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against 26 TAC 558.245 requirements.',
    },
    stateNotes: [
      '26 TAC 558.245 requires written staffing policies addressing personnel qualifications and supervision',
      'Policy violations are among the most common HHSC survey deficiencies in Texas',
      'Policies must be reviewed and updated annually; document the review date',
      'Keep a master policy manual accessible at your agency location for HHSC surveyor review',
      'Ensure all staff sign acknowledgment of policy receipt during orientation',
    ],
  },

  {
    id: 'TX-A5',
    itemId: 'A5',
    stateCode: 'TX',
    title: 'Quality Assurance and Performance Improvement (QAPI)',
    description: 'Texas requires a documented QAPI program per 26 TAC 558.287',
    severity: 'medium',
    // CORRECTED: Was 558.261, actual section is 558.287
    regulationCode: '26 TAC 558.287',
    regulationName: 'QAPI',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented Quality Assurance and Performance Improvement (QAPI) program?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive QAPI program with committee meetings' },
          { value: 'partial', label: 'Partial QAPI program' },
          { value: 'no', label: 'No QAPI program' },
        ],
        required: true,
        helpText: 'QAPI must address all categories of service per 26 TAC 558.287',
      },
      {
        id: 'q2',
        question: 'Does your QAPI Committee meet at least twice per year?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 26 TAC 558.287(c), QAPI Committee must meet twice a year or more often if needed',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'documented',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets Texas 26 TAC 558.287 requirements with committee meetings at least twice yearly.',
      atRisk: 'QAPI program is incomplete. Ensure program addresses all service categories and committee meets at least twice yearly per 26 TAC 558.287.',
      nonCompliant: 'No QAPI program in place. This violates 26 TAC 558.287. Implement QAPI immediately.',
      unknown: 'Review QAPI program against 26 TAC 558.287 requirements.',
    },
    stateNotes: [
      'QAPI Committee must meet at least twice per year (26 TAC 558.287(c))',
      'Program must be ongoing and focused on measurable client outcomes',
      'Must include analysis of active and closed records',
      'QAPI violations are consistently in top 10 HHSC survey deficiencies',
    ],
  },

  {
    id: 'TX-A6',
    itemId: 'A6',
    stateCode: 'TX',
    title: 'Emergency Preparedness and AlertMedia Registration',
    description: 'Texas requires emergency preparedness plan per 26 TAC 558.256',
    severity: 'high',
    // CORRECTED: Was 558.262, actual section is 558.256
    regulationCode: '26 TAC 558.256',
    regulationName: 'Emergency Preparedness Planning and Implementation',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov/providers/long-term-care-providers/home-community-support-services-agencies-hcssa/hcssa-emergency-preparedness',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you registered with AlertMedia for HHSC emergency notifications?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, registered with AlertMedia' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
        helpText: 'Per Provider Letter 2025-01, all HCSSAs must register with AlertMedia',
      },
      {
        id: 'q3',
        question: 'Does your plan address all four phases of emergency management?',
        inputType: 'checklist',
        options: [
          { value: 'mitigation', label: 'Mitigation - actions to eliminate/reduce disaster probability' },
          { value: 'preparedness', label: 'Preparedness - actions in anticipation of disaster' },
          { value: 'response', label: 'Response - actions during/after disaster' },
          { value: 'recovery', label: 'Recovery - returning to normal operations' },
        ],
        helpText: 'Per 26 TAC 558.256, plan must describe actions in each phase',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'documented',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Plan must address all four phases per 26 TAC 558.256
      { condition: 'countAtLeast', questionId: 'q3', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q3', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan and AlertMedia registration meet Texas 26 TAC 558.256 requirements.',
      atRisk: 'Emergency preparedness needs attention. Ensure plan addresses all four phases (mitigation, preparedness, response, recovery) and AlertMedia registration is current.',
      nonCompliant: 'No emergency preparedness plan in place or plan missing required phases. This violates 26 TAC 558.256. Register with AlertMedia per PL 2025-01.',
      unknown: 'Review emergency preparedness requirements and AlertMedia registration.',
    },
    stateNotes: [
      'AlertMedia registration required per Provider Letter 2025-01',
      'Plan must address hurricanes, tornadoes, power outages common in Texas',
      'Plan must include client notification and triage procedures',
      'Must designate disaster coordinator and alternate by title',
      'Must include continuity of operations business plan',
    ],
  },

  {
    id: 'TX-A7',
    itemId: 'A7',
    stateCode: 'TX',
    title: 'Insurance Coverage',
    description: 'Texas recommends but does not mandate certain insurance coverage',
    severity: 'medium',
    regulationCode: '26 TAC 558',
    regulationName: 'Insurance Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl', label: 'General liability insurance' },
          { value: 'prof', label: 'Professional liability insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance' },
          { value: 'bond', label: 'Surety bond' },
        ],
        required: true,
        helpText: 'Note: Workers\' compensation is NOT mandatory in Texas but strongly recommended',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage is comprehensive. Note: Workers\' comp is optional in Texas but recommended.',
      atRisk: 'Your insurance may be inadequate. While Texas doesn\'t mandate specific coverage, liability insurance is strongly recommended.',
      nonCompliant: 'Inadequate insurance coverage creates significant business risk. Obtain general and professional liability insurance.',
      unknown: 'Review insurance coverage for adequacy.',
    },
    stateNotes: [
      'Texas is one of the few states where workers\' compensation is OPTIONAL',
      'However, without workers\' comp, you lose certain legal protections',
      'General and professional liability insurance strongly recommended',
      'Some HHSC contracts may require specific coverage levels',
    ],
  },

  {
    id: 'TX-A8',
    itemId: 'A8',
    stateCode: 'TX',
    title: 'Record Retention (26 TAC 558.301)',
    description: 'Texas requires 5 years record retention for client records',
    severity: 'medium',
    regulationCode: '26 TAC 558.301',
    regulationName: 'Client Records',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain client records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets TX requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Texas requires 5 years retention after discharge per 26 TAC 558.301',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Texas\'s 5-year requirement under 26 TAC 558.301.',
      atRisk: 'Texas requires 5-year record retention per 26 TAC 558.301. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Texas\'s 5-year requirement under 26 TAC 558.301.',
      unknown: 'Review record retention requirements under 26 TAC 558.301.',
    },
    stateNotes: [
      '26 TAC 558.301 requires client records retained for 5 years after discharge',
      'For minors, retain records until 5 years after client reaches age 18',
      'Electronic records must be maintained in readable format with backup per 26 TAC 558.301(d)',
      'Records must be available to HHSC within 24 hours of request during investigations',
      'Orderly transfer of records required if agency closes per HSC Chapter 142',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'TX-B1',
    itemId: 'B1',
    stateCode: 'TX',
    title: 'Personnel File Requirements (26 TAC 558.246)',
    description: 'Texas requires specific documents in each attendant\'s personnel file',
    severity: 'high',
    regulationCode: '26 TAC 558.246',
    regulationName: 'Personnel Records',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY attendant\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'dps', label: 'DPS criminal history check results' },
          { value: 'emr', label: 'Employee Misconduct Registry (EMR) check' },
          { value: 'nar', label: 'Nurse Aide Registry check (if applicable)' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'w4', label: 'W-4 tax form' },
          { value: 'orientation', label: 'Orientation documentation' },
          { value: 'competency', label: 'Competency verification' },
          { value: 'job_desc', label: 'Signed job description' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Texas-required documentation per 26 TAC 558.246.',
      atRisk: 'Some personnel files are missing required documents. Texas requires: DPS check, EMR check, orientation, and competency records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 26 TAC 558.246. HHSC surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 26 TAC 558.246 requirements.',
    },
    stateNotes: [
      '26 TAC 558.246 requires personnel records for each employee with client contact',
      'DPS criminal history check and EMR check must be completed BEFORE employee begins client services',
      'Competency verification documentation required per 26 TAC 558.404(c)',
      'Personnel files must be maintained at agency location and available for HHSC survey',
      'Retain personnel records for 5 years after employee termination per 26 TAC 558.246(c)',
    ],
  },

  {
    id: 'TX-B2',
    itemId: 'B2',
    stateCode: 'TX',
    title: 'Attendant Qualifications (26 TAC 558.404)',
    description: 'Texas requires attendants to be 18+ with demonstrated competency',
    severity: 'high',
    regulationCode: '26 TAC 558.404(c)',
    regulationName: 'Attendant Qualifications',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL your attendants at least 18 years of age?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, all are 18+' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
        helpText: 'Per 26 TAC 558.404(c), attendants must be at least 18 years of age',
      },
      {
        id: 'q2',
        question: 'Do you verify competency for ALL attendants before assignment?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, competency verified for all before assignment' },
          { value: 'most', label: 'Most verified, some pending' },
          { value: 'no', label: 'No formal competency verification' },
        ],
        required: true,
        helpText: 'Per 26 TAC 558.404(c), attendants must demonstrate competency',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All attendants meet Texas 26 TAC 558.404(c) qualifications (18+, competency verified).',
      atRisk: 'Some attendants may not have competency verification documented. Complete verification for all.',
      nonCompliant: 'Attendants do not meet minimum qualifications per 26 TAC 558.404(c). Verify age and competency for all.',
      unknown: 'Verify all attendants meet 26 TAC 558.404(c) requirements.',
    },
    stateNotes: [
      '26 TAC 558.404(c) requires attendants be at least 18 years old',
      'Competency must be demonstrated before attendant provides services to clients',
      'Texas does not require specific certifications (like CNA) for PAS attendants',
      'Verify age with government-issued ID; keep copy in personnel file',
      'Competency verification should address tasks specific to client care needs',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'TX-C1',
    itemId: 'C1',
    stateCode: 'TX',
    title: 'DPS Criminal History Check (26 TAC 558.247)',
    description: 'Texas requires criminal history checks for all staff with client contact',
    severity: 'high',
    regulationCode: '26 TAC 558.247',
    regulationName: 'Criminal History Check Requirements',
    agencyName: 'Texas Department of Public Safety',
    agencyAbbr: 'DPS',
    agencyPhone: '(512) 424-2000',
    agencyUrl: 'https://www.dps.texas.gov/section/crime-records',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff with client contact or access to client records have a DPS criminal history check on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have DPS checks completed' },
          { value: 'most', label: 'Most have checks, some pending' },
          { value: 'no', label: 'No / Not consistently maintained' },
        ],
        required: true,
        helpText: 'Criminal history check required upon hire per 26 TAC 558.247',
      },
      {
        id: 'q2',
        question: 'Do you maintain a criminal history check log as prescribed by HHSC?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, log maintained per Provider Letter 2019-01' },
          { value: 'no', label: 'No log / individual printouts only' },
        ],
        required: true,
        helpText: 'Per PL 2019-01, providers may keep a log as evidence of compliance',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'DPS criminal history checks are complete for all staff per 26 TAC 558.247.',
      atRisk: 'Some staff may be missing criminal history checks or documentation is incomplete. Complete checks for all staff with client contact.',
      nonCompliant: 'Staff are working without required criminal history checks. This violates 26 TAC 558.247. Complete DPS checks for all staff immediately.',
      unknown: 'Audit criminal history check documentation per 26 TAC 558.247.',
    },
    stateNotes: [
      'DPS check required upon hire',
      'Provider Letter 2019-01 allows log format instead of individual printouts',
      'Health & Safety Code §250.006 lists barring convictions',
      'FBI check not required for PAS category (unlike hospice)',
    ],
  },

  {
    id: 'TX-C2',
    itemId: 'C2',
    stateCode: 'TX',
    title: 'Employee Misconduct Registry (EMR) Check',
    description: 'Texas requires verification against the Employee Misconduct Registry',
    severity: 'high',
    regulationCode: '26 TAC Chapter 561',
    regulationName: 'EMR Check Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Employee Misconduct Registry (EMR) for ALL new hires before they begin work?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, EMR checked for all before start date' },
          { value: 'most', label: 'Usually checked, sometimes after start' },
          { value: 'no', label: 'No / Not aware of EMR requirement' },
        ],
        required: true,
        helpText: 'EMR check required per 26 TAC Chapter 561',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee Misconduct Registry checks are completed for all new hires per 26 TAC Chapter 561.',
      atRisk: 'EMR checks should be completed BEFORE employees begin work. Update your process.',
      nonCompliant: 'EMR checks are not being conducted. This violates 26 TAC Chapter 561. Implement EMR screening immediately.',
      unknown: 'Verify EMR check process is in place.',
    },
    stateNotes: [
      'EMR check required per 26 TAC Chapter 561 and HSC Chapter 253 before employee provides direct care',
      'EMR is free and accessible online through HHSC website',
      'Document EMR check date and results in personnel file',
      'Employees on EMR for abuse, neglect, or exploitation are barred from employment',
      'Agencies must report substantiated findings of misconduct to EMR within 10 days',
    ],
  },

  {
    id: 'TX-C3',
    itemId: 'C3',
    stateCode: 'TX',
    title: 'Nurse Aide Registry Check',
    description: 'Texas requires verification of nurse aides against the Nurse Aide Registry',
    severity: 'high',
    regulationCode: 'HSC Chapter 250',
    regulationName: 'Nurse Aide Registry Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'If you employ CNAs, do you verify them on the Texas Nurse Aide Registry?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all CNAs verified before assignment' },
          { value: 'most', label: 'Most verified, some pending' },
          { value: 'na', label: 'N/A - We do not employ CNAs' },
          { value: 'no', label: 'No / Not verified' },
        ],
        required: true,
        helpText: 'Nurse Aide Registry check required per HSC Chapter 250',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['all', 'na'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Nurse Aide Registry checks are completed as required per HSC Chapter 250.',
      atRisk: 'Some CNAs may not be verified. Complete registry verification for all CNAs.',
      nonCompliant: 'CNAs are not being verified against the Nurse Aide Registry. This violates HSC Chapter 250.',
      unknown: 'Verify CNA registry check process is in place.',
    },
    stateNotes: [
      'HSC Chapter 250 requires verification of nurse aides against Nurse Aide Registry',
      'NAR check is free and accessible online through HHSC website',
      'Check verifies certification status and screens for findings of abuse/neglect',
      'PAS agencies are not required to employ CNAs; this applies only if CNAs are employed',
      'Document NAR verification date and results in personnel file for each CNA',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'TX-D1',
    itemId: 'D1',
    stateCode: 'TX',
    title: 'TB Screening (Provider Letter 2020-25)',
    description: 'Texas recommends but does not require TB screening - follow CDC guidelines',
    severity: 'medium',
    regulationCode: 'PL 2020-25',
    regulationName: 'TB Screening Recommendations',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct TB screening for your attendants?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all attendants are TB screened' },
          { value: 'some', label: 'Some attendants are screened' },
          { value: 'no', label: 'No TB screening conducted' },
        ],
        required: true,
        helpText: 'Per PL 2020-25, HHSC encourages following CDC guidelines for TB screening',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening is conducted for all attendants following CDC guidelines per PL 2020-25.',
      atRisk: 'TB screening is inconsistent. Consider screening all attendants per CDC guidelines.',
      nonCompliant: 'No TB screening is conducted. While not strictly mandated, HHSC strongly recommends TB screening per PL 2020-25.',
      unknown: 'Review TB screening recommendations in Provider Letter 2020-25.',
    },
    stateNotes: [
      'TB screening is RECOMMENDED but not mandatory in Texas for PAS agencies',
      'Provider Letter 2020-25 recommends following CDC guidelines',
      'Many Texas contracts and payers may require TB screening',
      'Best practice is to conduct TB screening at hire',
    ],
  },

  {
    id: 'TX-D2',
    itemId: 'D2',
    stateCode: 'TX',
    title: 'Infection Control Practices',
    description: 'Texas requires infection control procedures',
    severity: 'high',
    regulationCode: '26 TAC 558',
    regulationName: 'Infection Control Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have written infection control procedures that include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'ppe', label: 'Personal protective equipment (PPE) guidelines' },
          { value: 'bloodborne', label: 'Bloodborne pathogen procedures' },
          { value: 'training', label: 'Infection control training for staff' },
          { value: 'reporting', label: 'Communicable disease reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets Texas requirements.',
      atRisk: 'Infection control program is incomplete. Update procedures to include all required elements.',
      nonCompliant: 'Infection control program is inadequate. Immediately develop comprehensive infection control policies.',
      unknown: 'Review infection control procedures for completeness.',
    },
    stateNotes: [
      'Infection control policies required as part of 26 TAC 558.245 staffing policies',
      'OSHA bloodborne pathogen standard (29 CFR 1910.1030) applies to Texas employers',
      'Train all staff on hand hygiene and PPE use during orientation',
      'Maintain infection control supplies (gloves, hand sanitizer, masks) for staff use',
      'Document infection control training in personnel files',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'TX-E1',
    itemId: 'E1',
    stateCode: 'TX',
    title: 'Attendant Orientation (26 TAC 558.404)',
    description: 'Texas requires orientation for all attendants before client assignment',
    severity: 'high',
    regulationCode: '26 TAC 558.404',
    regulationName: 'Orientation Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL new attendants complete orientation BEFORE providing services to clients?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete orientation before client assignment' },
          { value: 'most', label: 'Most complete orientation, some start early' },
          { value: 'no', label: 'No formal orientation program' },
        ],
        required: true,
        helpText: 'Orientation required per 26 TAC 558.404 before providing services',
      },
      {
        id: 'q2',
        question: 'Does orientation include client-specific training for each assignment?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, client-specific orientation provided' },
          { value: 'no', label: 'No, general orientation only' },
        ],
        required: true,
        helpText: 'Attendants must be oriented to each individual client per 26 TAC 558.404',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Attendant orientation meets Texas 26 TAC 558.404 requirements including client-specific training.',
      atRisk: 'Orientation may be incomplete. Ensure all attendants are oriented before client assignment AND receive client-specific training.',
      nonCompliant: 'No formal orientation program exists. This violates 26 TAC 558.404. Implement orientation immediately.',
      unknown: 'Review orientation program against 26 TAC 558.404 requirements.',
    },
    stateNotes: [
      'Texas does not mandate specific training HOURS like California',
      'Orientation must include agency policies AND client-specific information',
      'Orientation must be completed BEFORE or on the day attendant begins services',
      'Documentation of orientation required in personnel file',
    ],
  },

  {
    id: 'TX-E2',
    itemId: 'E2',
    stateCode: 'TX',
    title: 'Continuing Education Plan',
    description: 'Texas requires a written continuing education plan for staff',
    severity: 'medium',
    regulationCode: '26 TAC 558.245',
    regulationName: 'Continuing Education Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written continuing education plan for staff?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, documented CE plan implemented' },
          { value: 'informal', label: 'Informal training, not documented plan' },
          { value: 'no', label: 'No continuing education plan' },
        ],
        required: true,
        helpText: 'CE plan required as part of HCSSA application per 26 TAC 558',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'informal', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Continuing education plan is documented and implemented.',
      atRisk: 'CE plan should be formally documented. Create written plan per 26 TAC 558.',
      nonCompliant: 'No continuing education plan exists. This was required as part of your license application.',
      unknown: 'Review continuing education plan requirements.',
    },
    stateNotes: [
      'Written CE plan required as part of initial HCSSA license application',
      'CE plan must address ongoing staff development and training needs',
      'Texas does not mandate specific annual training hours for attendants (unlike California)',
      'Document all training completed by staff with dates and topics',
      'Include ANE training, infection control, and client-specific care in CE plan',
    ],
  },

  {
    id: 'TX-E3',
    itemId: 'E3',
    stateCode: 'TX',
    title: 'Abuse/Neglect/Exploitation (ANE) Reporting Training',
    description: 'Texas requires training on ANE recognition and reporting',
    severity: 'high',
    regulationCode: '26 TAC 558.245',
    regulationName: 'ANE Reporting Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(800) 458-9858',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your attendants receive training on recognizing and reporting abuse, neglect, and exploitation?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all trained on ANE recognition and reporting' },
          { value: 'most', label: 'Most trained, some pending' },
          { value: 'no', label: 'No ANE training provided' },
        ],
        required: true,
        helpText: 'ANE reporting required per Human Resources Code Chapter 48',
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to the Texas Abuse Hotline?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, trained on hotline (800-458-9858)' },
          { value: 'no', label: 'No / Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on ANE recognition and reporting to Texas Abuse Hotline.',
      atRisk: 'Some staff may lack ANE training. Complete training for all and ensure familiarity with reporting hotline (800-458-9858).',
      nonCompliant: 'No ANE training provided. This violates Human Resources Code Chapter 48. Failure to report suspected abuse is a crime in Texas.',
      unknown: 'Verify all staff have received ANE training.',
    },
    stateNotes: [
      'Texas Abuse Hotline: 1-800-458-9858',
      'Failure to report suspected abuse is a criminal offense in Texas',
      'Reports can also be made online at txabusehotline.org',
      'CIMS (Critical Incident Management System) may be required for certain waiver providers',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'TX-F1',
    itemId: 'F1',
    stateCode: 'TX',
    title: 'Individual Service Plan (ISP) Requirements (26 TAC 558.404)',
    description: 'Texas requires an Individual Service Plan for each client',
    severity: 'high',
    regulationCode: '26 TAC 558.404(f)',
    regulationName: 'Individual Service Plan Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an Individual Service Plan (ISP) for EVERY client before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, ISP completed before first service' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal ISP process' },
        ],
        required: true,
        helpText: 'ISP required per 26 TAC 558.404(f) for PAS agencies',
      },
      {
        id: 'q2',
        question: 'Do your ISPs include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Types of services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Goals and outcomes' },
          { value: 'supervision', label: 'Supervision frequency' },
          { value: 'signature', label: 'Client/representative signature' },
          { value: 'date', label: 'Date of service plan' },
        ],
        helpText: 'Per 26 TAC 558.404(f)(2)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Individual Service Plans are completed before services begin with all required elements per 26 TAC 558.404(f).',
      atRisk: 'ISPs may be incomplete or created after services begin. 26 TAC 558.404(f) requires ISP before first service.',
      nonCompliant: 'Services are being provided without Individual Service Plans. This violates 26 TAC 558.404(f). Immediately create ISPs for all clients.',
      unknown: 'Review ISP process against 26 TAC 558.404(f) requirements.',
    },
    stateNotes: [
      '26 TAC 558.404(f) requires ISP before services begin',
      'ISP must include services, frequency, duration, goals, supervision frequency, and client signature',
      'Update ISP when client needs change or at least annually',
      'Client or legal representative must sign acknowledging receipt of ISP',
      'Keep current ISP in client record and provide copy to client/representative',
    ],
  },

  {
    id: 'TX-F2',
    itemId: 'F2',
    stateCode: 'TX',
    title: 'Supervision of Attendants (26 TAC 558.404(g))',
    description: 'Texas requires supervision of PAS attendants',
    severity: 'high',
    regulationCode: '26 TAC 558.404(g)',
    regulationName: 'Supervision Requirements',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy addressing supervision of attendants?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, written supervision policy with client/family input' },
          { value: 'no', label: 'No written policy' },
        ],
        required: true,
        helpText: 'Per 26 TAC 558.404(g), supervision policy must include client/family input on frequency',
      },
      {
        id: 'q2',
        question: 'Does your supervisor meet the qualifications in 26 TAC 558.404(g)(2)?',
        inputType: 'select',
        options: [
          { value: 'rn', label: 'Yes - Licensed Nurse' },
          { value: 'college', label: 'Yes - 2 years college study' },
          { value: 'experience', label: 'Yes - HS diploma + supervisory experience substitute' },
          { value: 'no', label: 'Not sure / May not meet qualifications' },
        ],
        required: true,
        helpText: 'Supervisor must be LN or have 2 years college (or experience substitute)',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'includes', questionId: 'q2', value: ['rn', 'college', 'experience'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervision meets Texas 26 TAC 558.404(g) requirements with qualified supervisor and written policy.',
      atRisk: 'Supervision policy or supervisor qualifications may need attention. Review 26 TAC 558.404(g) requirements.',
      nonCompliant: 'Supervision does not meet 26 TAC 558.404(g) requirements. Implement written policy and ensure supervisor qualifications.',
      unknown: 'Review supervision practices against 26 TAC 558.404(g) requirements.',
    },
    stateNotes: [
      'Supervisor must be: Licensed Nurse, OR 2 years college, OR HS diploma + supervisory experience (1 year experience = 1 year college)',
      'Written policy must include client/family input on supervision frequency',
      'Supervision must comply with Texas Board of Nursing rules for RN delegation (22 TAC Chapter 225)',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'TX-G1',
    itemId: 'G1',
    stateCode: 'TX',
    title: 'Client Record Requirements (26 TAC 558.301)',
    description: 'Texas requires specific documentation in client records',
    severity: 'high',
    regulationCode: '26 TAC 558.301',
    regulationName: 'Client Records',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do your client records include all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'isp', label: 'Individual Service Plan (ISP)' },
          { value: 'agreement', label: 'Service agreement' },
          { value: 'assessments', label: 'Assessments' },
          { value: 'service_notes', label: 'Service delivery notes' },
          { value: 'supervision', label: 'Supervision documentation' },
          { value: 'incident', label: 'Incident reports (if any)' },
          { value: 'consent', label: 'Consents and authorizations' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are client records accessible to HHSC surveyors during inspections?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, readily accessible (including electronic records)' },
          { value: 'no', label: 'No / May have accessibility issues' },
        ],
        required: true,
        helpText: 'Electronic records must be accessible to surveyors per 26 TAC 558.301',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Client records contain required documentation and are accessible per 26 TAC 558.301.',
      atRisk: 'Client records may be missing required elements. Review 26 TAC 558.301 requirements.',
      nonCompliant: 'Client records do not meet 26 TAC 558.301 requirements. Immediately update record-keeping practices.',
      unknown: 'Audit client records against 26 TAC 558.301 requirements.',
    },
    stateNotes: [
      '26 TAC 558.301 requires client records be complete, accurate, and current',
      'Electronic records acceptable but must be accessible to HHSC surveyors',
      'Each entry must identify the person making it (signature, initials, or electronic ID)',
      'Corrections must use single line strike-through; never delete or white-out entries',
      'Client record deficiencies are among top 10 HHSC survey findings in Texas',
    ],
  },

  {
    id: 'TX-G2',
    itemId: 'G2',
    stateCode: 'TX',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and training expirations',
    severity: 'medium',
    regulationCode: '26 TAC 558',
    regulationName: 'Record Maintenance',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'hcssa_license', label: 'HCSSA license (renews every 2 years)' },
          { value: 'admin_training', label: 'Administrator CE (12 hours annually)' },
          { value: 'insurance', label: 'Insurance policies' },
          { value: 'cpr', label: 'CPR/First Aid certifications (if applicable)' },
          { value: 'tb', label: 'TB screenings (if conducted)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'HCSSA license expires every 2 years; track expiration and submit renewal in TULIP 60+ days early',
      'Administrator CE (12 hours) required annually per 26 TAC 558.260',
      'Insurance policies typically renew annually; ensure no coverage gaps',
      'CPR/First Aid certifications (if required by your policies) typically expire every 2 years',
      'Set calendar reminders 90, 60, and 30 days before critical expirations',
    ],
  },

  {
    id: 'TX-G3',
    itemId: 'G3',
    stateCode: 'TX',
    title: 'Audit-Ready Documentation',
    description: 'Records organized for HHSC surveyor review',
    severity: 'medium',
    regulationCode: '26 TAC 558',
    regulationName: 'Survey Readiness',
    agencyName: 'Texas Health and Human Services Commission',
    agencyAbbr: 'HHSC',
    agencyPhone: '(512) 438-2630',
    agencyUrl: 'https://www.hhs.texas.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are your personnel and client files organized and accessible for HHSC inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any file within 5 minutes if HHSC arrived for a survey?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for HHSC inspection.',
      atRisk: 'File organization needs improvement. HHSC surveyors expect immediate access to records.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement standardized file structure immediately.',
      unknown: 'Review file organization for survey readiness.',
    },
    stateNotes: [
      'HHSC surveyors conduct unannounced surveys; records must be accessible immediately',
      'Maintain consistent file structure: personnel files, client files, policy manual, training records',
      'Electronic records must be viewable on-site; surveyors may not accept remote access',
      'Prepare a survey binder with key documents: license, administrator credentials, policies, emergency plan',
      'Conduct quarterly internal audits to identify and correct documentation gaps before HHSC survey',
    ],
  },
];

export default TEXAS_QUESTIONS;